import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDemoStore } from '../stores/demoStore'
import { useAuthStore } from '../stores/authStore'
import {
  X, Play, ChevronRight, ChevronLeft, CheckCircle, Circle,
  AlertTriangle, BarChart3, Users, ClipboardCheck, Key, Shield,
  Smartphone, Download, Copy,
} from 'lucide-react'

const STEPS = [
  {
    id: 0, title: 'Dashboard Overview', icon: BarChart3,
    path: '/dashboard',
    description: 'See your real-time HSEQ metrics: open incidents, overdue actions, upcoming audits, and safety performance trends.',
    action: 'View the live dashboard →',
    tip: 'The dashboard auto-refreshes and shows all activity across your organisation.',
  },
  {
    id: 1, title: 'Log an Incident', icon: AlertTriangle,
    path: '/incidents/new',
    description: 'Walk through the smart incident form — AI severity scoring, automatic notifications, and regulatory compliance checks.',
    action: 'Try logging an incident →',
    tip: 'Try "Near Miss" type to see the streamlined quick-capture form.',
  },
  {
    id: 2, title: 'Corrective Actions', icon: CheckCircle,
    path: '/corrective-actions',
    description: 'View open corrective actions, assign owners, set due dates, and track completion. All linked back to incidents.',
    action: 'View corrective actions →',
    tip: '5 demo actions are pre-loaded with different priorities and statuses.',
  },
  {
    id: 3, title: 'Permits to Work', icon: Key,
    path: '/permits',
    description: 'Manage hot work, confined space, and electrical isolation permits. Digital approval chains replace paper.',
    action: 'View active permits →',
    tip: 'Permits enforce safety steps before work begins and expire automatically.',
  },
  {
    id: 4, title: 'Conduct an Audit', icon: ClipboardCheck,
    path: '/audits',
    description: 'Run a compliance audit from a pre-built template. Add findings, photos, and generate a signed PDF report.',
    action: 'Start an audit →',
    tip: 'Audit templates are fully customisable per industry and site type.',
  },
  {
    id: 5, title: 'Team & Roles', icon: Users,
    path: '/workforce',
    description: 'View the full team hierarchy — 7 demo users from Admin to Viewer. Explore role-based access and permissions.',
    action: 'View workforce →',
    tip: 'Log in as different demo users to experience role-specific views.',
  },
  {
    id: 6, title: 'Analytics & Reports', icon: BarChart3,
    path: '/metrics',
    description: 'Explore pre-populated analytics: incident trends, corrective action rates, audit scores, and contractor performance.',
    action: 'View metrics →',
    tip: 'Export any report as PDF or CSV for stakeholder presentations.',
  },
]

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800) }}
      style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: copied ? '#22C55E' : '#9CA3AF', fontSize: '0.625rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}
    >
      <Copy size={10} /> {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

export default function DemoBanner() {
  const { isDemo, currentStep, totalSteps, nextStep, prevStep, setStep, companyName, mobileRequested, mobilePhone, mobileSmsSent, adminEmail } = useDemoStore()
  const { logout, refreshToken } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [mobileExpanded, setMobileExpanded] = useState(mobileRequested)

  // Real accounts have a refresh token; demo sessions use '' — never show banner for real logins
  if (!isDemo || dismissed || refreshToken !== '') return null

  const step = STEPS[currentStep] || STEPS[0]
  const progress = ((currentStep + 1) / totalSteps) * 100

  function goStep(s: typeof STEPS[0]) {
    setStep(s.id)
    navigate(s.path)
    setSidebarOpen(false)
  }

  function exitDemo() {
    logout()
    navigate('/demo')
  }

  return (
    <>
      {/* Top Banner */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        background: 'linear-gradient(90deg, #0052CC 0%, #0066FF 100%)',
        padding: '0.5rem 1rem',
        display: 'flex', alignItems: 'center', gap: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#BFDBFE', animation: 'pulse 2s infinite' }} />
          <span style={{ color: '#BFDBFE', fontSize: '0.8125rem', fontWeight: 600 }}>DEMO MODE</span>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8125rem' }}>
            — {companyName || 'Demo Company'} · No data will be saved permanently
          </span>
        </div>

        {/* Step progress */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
            Step {currentStep + 1}/{totalSteps}: {step.title}
          </span>
          <div style={{ width: 100, height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: '#BFDBFE', borderRadius: 2, transition: 'width 0.3s' }} />
          </div>
        </div>

        <button onClick={() => setSidebarOpen(true)} style={{
          display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.15)',
          border: 'none', borderRadius: 6, padding: '0.3rem 0.75rem', color: '#fff', cursor: 'pointer', fontSize: '0.8125rem',
        }}>
          <Play size={12} /> Guide
        </button>
        <button onClick={exitDemo} style={{
          display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.1)',
          border: 'none', borderRadius: 6, padding: '0.3rem 0.75rem', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '0.8125rem',
        }}>
          Exit Demo
        </button>
      </div>

      {/* Walkthrough Sidebar */}
      {sidebarOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex' }} onClick={() => setSidebarOpen(false)}>
          <div style={{ flex: 1 }} />
          <div
            style={{
              width: 360, background: '#fff', height: '100vh', boxShadow: '-4px 0 32px rgba(0,0,0,0.15)',
              overflowY: 'auto', display: 'flex', flexDirection: 'column',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '1.25rem', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 700, color: '#111827', fontSize: '1rem' }}>Demo Walkthrough</div>
                <div style={{ color: '#6B7280', fontSize: '0.75rem', marginTop: 2 }}>{currentStep + 1} of {totalSteps} steps</div>
              </div>
              <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4 }}>
                <X size={18} />
              </button>
            </div>

            {/* Progress bar */}
            <div style={{ height: 3, background: '#F3F4F6' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: '#0066FF', transition: 'width 0.3s' }} />
            </div>

            {/* Current step detail */}
            <div style={{ padding: '1.25rem', borderBottom: '1px solid #E5E7EB', background: '#F8FAFF' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.75rem' }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <step.icon size={16} color="#0066FF" />
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.9375rem' }}>{step.title}</div>
                </div>
              </div>
              <p style={{ color: '#374151', fontSize: '0.875rem', lineHeight: 1.6, margin: '0 0 0.75rem' }}>{step.description}</p>
              <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '0.625rem 0.75rem', fontSize: '0.8125rem', color: '#92400E' }}>
                💡 {step.tip}
              </div>
              <button onClick={() => { navigate(step.path); setSidebarOpen(false) }}
                style={{ width: '100%', marginTop: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#0066FF', color: '#fff', border: 'none', borderRadius: 8, padding: '0.625rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
                {step.action} <ChevronRight size={14} />
              </button>
            </div>

            {/* All steps list */}
            <div style={{ padding: '0.75rem', flex: 1 }}>
              <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0.5rem 0.5rem 0.25rem' }}>
                All Steps
              </div>
              {STEPS.map((s, i) => {
                const done = i < currentStep
                const active = i === currentStep
                return (
                  <button key={s.id} onClick={() => goStep(s)} style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '0.625rem',
                    padding: '0.625rem 0.5rem', borderRadius: 8, border: 'none', cursor: 'pointer', textAlign: 'left',
                    background: active ? '#EFF6FF' : 'transparent',
                    marginBottom: 2,
                  }}>
                    {done
                      ? <CheckCircle size={18} color="#10B981" />
                      : active
                      ? <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #0066FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#0066FF' }} />
                        </div>
                      : <Circle size={18} color="#D1D5DB" />
                    }
                    <span style={{ fontSize: '0.8125rem', fontWeight: active ? 600 : 400, color: active ? '#0066FF' : done ? '#6B7280' : '#374151' }}>
                      {i + 1}. {s.title}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* Nav buttons */}
            <div style={{ padding: '1rem', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => { prevStep(); navigate(STEPS[Math.max(0, currentStep - 1)].path) }}
                disabled={currentStep === 0}
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '0.5rem', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', cursor: currentStep > 0 ? 'pointer' : 'default', color: currentStep > 0 ? '#374151' : '#D1D5DB', fontSize: '0.875rem' }}>
                <ChevronLeft size={14} /> Prev
              </button>
              <button onClick={() => { nextStep(); navigate(STEPS[Math.min(totalSteps - 1, currentStep + 1)].path) }}
                disabled={currentStep === totalSteps - 1}
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '0.5rem', background: '#0066FF', border: 'none', borderRadius: 8, cursor: currentStep < totalSteps - 1 ? 'pointer' : 'default', color: '#fff', fontSize: '0.875rem', fontWeight: 600, opacity: currentStep === totalSteps - 1 ? 0.5 : 1 }}>
                Next <ChevronRight size={14} />
              </button>
            </div>

            {/* Demo credentials */}
            <div style={{ padding: '0.875rem 1rem', background: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}>
              <div style={{ fontSize: '0.6875rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Demo Credentials</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: '0.625rem' }}>
                {adminEmail && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '0.375rem 0.625rem' }}>
                    <div>
                      <div style={{ fontSize: '0.5625rem', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</div>
                      <div style={{ fontSize: '0.6875rem', color: '#374151', fontFamily: 'monospace' }}>{adminEmail}</div>
                    </div>
                    <CopyBtn text={adminEmail} />
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '0.375rem 0.625rem' }}>
                  <div>
                    <div style={{ fontSize: '0.5625rem', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Password (all 8 users)</div>
                    <div style={{ fontSize: '0.6875rem', color: '#374151', fontFamily: 'monospace' }}>Demo2026!</div>
                  </div>
                  <CopyBtn text="Demo2026!" />
                </div>
              </div>
              <a href="/download/hseq-intelligence.apk" download
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#0F172A', color: '#fff', textDecoration: 'none', borderRadius: 7, padding: '0.4375rem 0.75rem', fontSize: '0.6875rem', fontWeight: 600 }}>
                <Download size={13} /> Download Android App (APK)
              </a>
            </div>

            {/* Mobile setup panel */}
            {mobileRequested && (
              <div style={{ borderTop: '2px solid #DBEAFE', background: '#EFF6FF' }}>
                <button
                  onClick={() => setMobileExpanded(e => !e)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Smartphone size={15} color="#0066FF" />
                    <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1D4ED8' }}>Mobile App Setup</span>
                    {mobileSmsSent && <span style={{ fontSize: '0.625rem', background: '#DCFCE7', color: '#15803D', fontWeight: 600, borderRadius: 99, padding: '1px 6px' }}>Link sent ✓</span>}
                  </div>
                  <span style={{ color: '#93C5FD', fontSize: '0.75rem' }}>{mobileExpanded ? '▲' : '▼'}</span>
                </button>

                {mobileExpanded && (
                  <div style={{ padding: '0 1rem 1rem' }}>
                    {mobileSmsSent
                      ? <p style={{ fontSize: '0.75rem', color: '#1D4ED8', margin: '0 0 0.75rem', background: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '0.375rem 0.5rem' }}>
                          ✓ Download link sent to <strong>{mobilePhone}</strong>
                        </p>
                      : <p style={{ fontSize: '0.75rem', color: '#DC2626', margin: '0 0 0.75rem', background: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '0.375rem 0.5rem' }}>
                          SMS could not be sent. Download the APK below.
                        </p>
                    }

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {[
                        'Open the download link on your phone',
                        'Settings → Apps → Special app access → Install unknown apps → allow Chrome',
                        'Tap the downloaded APK and install',
                        'Open the app and sign in with the credentials above',
                      ].map((text, i) => (
                        <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#DBEAFE', color: '#1D4ED8', fontSize: '0.5625rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                            {i + 1}
                          </div>
                          <p style={{ color: '#374151', fontSize: '0.6875rem', margin: 0, lineHeight: 1.55 }}>{text}</p>
                        </div>
                      ))}
                    </div>

                    <a href="/download/hseq-intelligence.apk" download
                      style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: '0.75rem', background: '#0066FF', color: '#fff', textDecoration: 'none', borderRadius: 7, padding: '0.4375rem 0.75rem', fontSize: '0.6875rem', fontWeight: 600 }}>
                      <Download size={13} /> Download APK (~24 MB)
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
